import React, { memo, useEffect } from 'react';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import {
  CALENDAR_DATE_CELL_ANIMATIONS,
  calendarDateCellAnimationTransform,
  CALENDAR_DATE_CELL_ANIMATION_TWO_PI,
  type CalendarDateCellAnimationPreset,
} from './calendarDateCellVisuals';

interface CalendarDateCellAnimationProps {
  preset: CalendarDateCellAnimationPreset;
  children: React.ReactNode;
  /** When true, skip looping transforms (e.g. while the calendar scroll view is moving). */
  suppressed?: boolean;
}

/** Seamless cosine-phase loop — one shared value, UI-thread only. */
function CalendarDateCellAnimationInner({ preset, children, suppressed = false }: CalendarDateCellAnimationProps): React.ReactElement {
  const reduce_motion = useReducedMotion();
  const phase = useSharedValue(0);
  const { cycle_ms, scale_from, scale_to, translate_y_from, translate_y_to } = CALENDAR_DATE_CELL_ANIMATIONS[preset];
  const animations_active = !reduce_motion && !suppressed;

  useEffect(() => {
    cancelAnimation(phase);
    if (!animations_active) {
      phase.value = 0;
      return;
    }
    phase.value = 0;
    phase.value = withRepeat(
      withTiming(CALENDAR_DATE_CELL_ANIMATION_TWO_PI, { duration: cycle_ms, easing: Easing.linear }),
      -1,
      false,
    );
    return () => cancelAnimation(phase);
  }, [animations_active, cycle_ms, phase]);

  const animated_style = useAnimatedStyle(() => {
    'worklet';
    const { scale, translateY } = calendarDateCellAnimationTransform(
      phase.value,
      scale_from,
      scale_to,
      translate_y_from,
      translate_y_to,
    );
    return { transform: [{ translateY }, { scale }] };
  }, [scale_from, scale_to, translate_y_from, translate_y_to]);

  if (!animations_active) {
    return <>{children}</>;
  }

  return (
    <Animated.View collapsable={false} style={animated_style}>
      {children}
    </Animated.View>
  );
}

export const CalendarDateCellAnimation = memo(CalendarDateCellAnimationInner);
